import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router";

/**
 * Batch 13 (day-one feedback GRF-16): a list or board remembers where it was scrolled, per browser history entry, so
 * Back from an item returns to the same row instead of the top. Positions are kept in sessionStorage under the router's
 * `location.key`, which is new for every navigation and comes back unchanged on Back/Forward: a fresh visit to the same
 * URL starts at the top, as it always did; only a return restores. The store keeps the most recent entries only.
 */
const STORE = "giraffe.historyScroll";
const MAX_ENTRIES = 60;
/** How long a return holds the position while the page fills in and the browser finishes its own restore. */
const SETTLE_MS = 1500;
/** Anything the person does to scroll themselves ends the restore. */
const USER_SCROLL = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

interface Position {
  top: number;
  left: number;
}

type Store = { order: string[]; positions: Record<string, Position> };

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readStore(): Store {
  try {
    const raw = storage()?.getItem(STORE);
    const parsed = raw ? (JSON.parse(raw) as Partial<Store>) : null;
    if (parsed && Array.isArray(parsed.order) && parsed.positions && typeof parsed.positions === "object") return parsed as Store;
  } catch {
    // A damaged entry is simply forgotten.
  }
  return { order: [], positions: {} };
}

export function readHistoryScroll(id: string): Position | undefined {
  const position = readStore().positions[id];
  return position && Number.isFinite(position.top) && Number.isFinite(position.left) ? position : undefined;
}

export function writeHistoryScroll(id: string, position: Position): void {
  const store = readStore();
  store.order = [...store.order.filter((entry) => entry !== id), id];
  store.positions[id] = position;
  while (store.order.length > MAX_ENTRIES) {
    const oldest = store.order.shift();
    if (oldest !== undefined) delete store.positions[oldest];
  }
  try {
    storage()?.setItem(STORE, JSON.stringify(store));
  } catch {
    // Storage full or unavailable: the page still works, it just starts at the top on return.
  }
}

function scrollElement(element: HTMLElement, position: Position): void {
  element.scrollTop = position.top;
  element.scrollLeft = position.left;
}

/**
 * Remember and restore one scroll position for this history entry. `target` is an element ref, or "window" for the page
 * itself. `name` tells apart several scrollers on one page (null switches the hook off). `ready` is true once the content is there to scroll to (the
 * first page of rows has rendered), so a position is restored once per return and never fights the user afterwards.
 */
export function useHistoryScroll(target: RefObject<HTMLElement | null> | "window", name: string | null, ready: boolean): void {
  const { key, pathname } = useLocation();
  const id = `${key}:${name}`;
  const restored = useRef<string | null>(null);
  const previous = useRef<{ id: string; pathname: string } | null>(null);
  // The last position seen (scrolled to, or restored), kept so a new entry can inherit it without reading the layout
  // during a commit: a forced layout there lets the browser move focus off an element a drawer is about to remember.
  const live = useRef<Position | null>(null);

  useEffect(() => {
    if (name === null || !ready || restored.current === id) return;
    restored.current = id;
    const saved = readHistoryScroll(id);
    if (!saved) return;
    live.current = saved;
    // For a moment after a return the position is held: parts of the page can still be loading (so it may be too short
    // to reach the position yet), and the browser's own restore can land a stale position after ours. Each frame puts
    // it back if it moved; anything the person does themselves (wheel, touch, a key, a click) ends the hold at once.
    const element = target === "window" ? null : target.current;
    if (target !== "window" && !element) return;
    const apply = () => {
      const now = element ? { top: element.scrollTop, left: element.scrollLeft } : { top: window.scrollY, left: window.scrollX };
      if (Math.abs(now.top - saved.top) < 2 && Math.abs(now.left - saved.left) < 2) return;
      if (element) scrollElement(element, saved);
      else window.scrollTo(saved.left, saved.top);
    };
    let frame = 0;
    const started = performance.now();
    const hold = () => {
      apply();
      frame = performance.now() - started < SETTLE_MS ? window.requestAnimationFrame(hold) : 0;
    };
    const stop = () => {
      window.cancelAnimationFrame(frame);
      for (const type of USER_SCROLL) window.removeEventListener(type, stop, true);
    };
    for (const type of USER_SCROLL) window.addEventListener(type, stop, { capture: true, passive: true });
    hold();
    return stop;
  }, [name, ready, id, target]);

  // A layout effect, so the listener for the entry being left is removed while the next page is committed: the scroll
  // the browser makes when that page is shorter must not be recorded as the old entry's position.
  useLayoutEffect(() => {
    if (name === null) return;
    const element = target === "window" ? null : target.current;
    if (target !== "window" && !element) return;
    // The position is read at scroll time (the element may already be detached when this effect is cleaned up) and
    // written at most once a frame, plus once more on the way out so a click straight after scrolling is not lost.
    let frame = 0;
    let latest: Position | null = null;
    // D-D13-02: a new history entry on the same page (the board's card preview, a filter) starts where the page is: it
    // inherits the live position, so Back to that entry, from an item opened through the preview, lands there too.
    const before = previous.current;
    previous.current = { id, pathname };
    if (before && before.id !== id && before.pathname === pathname && live.current && !readHistoryScroll(id)) {
      writeHistoryScroll(id, live.current);
      // Nothing to put back: the page is already there, so no hold (it would fight the person's next scroll).
      restored.current = id;
    }
    let kept: Position | null = readHistoryScroll(id) ?? null;
    const flush = () => {
      window.cancelAnimationFrame(frame);
      frame = 0;
      if (latest) writeHistoryScroll(id, latest);
      latest = null;
    };
    const save = () => {
      const now = element ? { top: element.scrollTop, left: element.scrollLeft } : { top: window.scrollY, left: window.scrollX };
      // When the content gets shorter (the next page replacing this one, a list refetching) the browser pulls the
      // position up to the new bottom and reports it as a scroll. That is not where the person was, so it is not kept.
      const root = element ?? document.documentElement;
      const bottom = root.scrollHeight - root.clientHeight;
      if (kept && now.top < kept.top && now.top >= bottom - 1) return;
      kept = now;
      latest = now;
      live.current = now;
      if (!frame) frame = window.requestAnimationFrame(flush);
    };
    const source: HTMLElement | Window = element ?? window;
    source.addEventListener("scroll", save, { passive: true });
    return () => {
      source.removeEventListener("scroll", save);
      flush();
    };
  }, [name, id, pathname, target, ready]);
}

/**
 * The page's own scroll, for every screen (mounted once, in the app shell). The app takes over from the browser's built-in
 * restore, which in a single-page app restores before the page has re-rendered and so lands short or not at all; Back and
 * Forward return each history entry to where it was, held while the page fills in. Opening a different page starts at its
 * top (it used to keep the previous page's scroll, so an item opened from far down a list opened far down its own page);
 * a change of query only, such as a filter or the board's item preview, never moves the page.
 */
export function usePageHistoryScroll(): void {
  const location = useLocation();
  const navigation = useNavigationType();
  const path = useRef(location.pathname);

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;
    const before = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = before;
    };
  }, []);

  useEffect(() => {
    const moved = path.current !== location.pathname;
    path.current = location.pathname;
    if (navigation === NavigationType.Push && moved && !location.hash) window.scrollTo(0, 0);
  }, [location.key, location.pathname, location.hash, navigation]);

  useHistoryScroll("window", "page", true);
}
